// @ts-strict-ignore
import {Component, Injector, OnInit} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";

import {
	AudioDeviceInputChannelType,
	AudioDeviceOutputChannelType,
	AudioInputChannel,
	AudioOutputChannel,
	MasterOfCeremonyMapping,
	NetworkAudioInputChannel,
	PhoneAudioInputChannel,
} from "@models";
import {Translations} from "@translations";
import {FormControl} from "@angular/forms";
import {SimAudioChannelService} from "@services";

export interface MASTER_OF_CEREMONY_AUDIO_CHANNEL_SELECTION_INPUT extends MatDialogDataBaseInput {
	masterOfCeremonyMapping: MasterOfCeremonyMapping;
}

export interface MASTER_OF_CEREMONY_AUDIO_CHANNEL_SELECTION_OUTPUT extends MatDialogDataBaseOutput {
	updatedMasterOfCeremonyMapping: MasterOfCeremonyMapping;
}

@Component({
	selector: "sim-master-of-ceremony-audio-channel-selection-dialog",
	templateUrl: "./master-of-ceremony-audio-channel-selection-dialog.component.html",
	styleUrl: "./master-of-ceremony-audio-channel-selection-dialog.component.scss",
	standalone: false,
})
export class MasterOfCeremonyAudioChannelSelectionDialogComponent
	extends BasicMaterialDialog<
		MasterOfCeremonyAudioChannelSelectionDialogComponent,
		MASTER_OF_CEREMONY_AUDIO_CHANNEL_SELECTION_INPUT,
		MASTER_OF_CEREMONY_AUDIO_CHANNEL_SELECTION_OUTPUT
	>
	implements OnInit
{
	T = Translations;

	public vogInputChannelId: string = null;
	public vogNetworkInputChannelId: string = null;

	public vopInputChannelId: string = null;
	public vopNetworkInputChannelId: string = null;

	public vttInputChannelId: string = null;
	public vttNetworkInputChannelId: string = null;

	public directOutputChannelIdFC: FormControl<string> = new FormControl<string>(null);
	public websocketOutputChannelIdFC: FormControl<string> = new FormControl<string>(null);

	public availableVogAudioInputChannels: AudioInputChannel[] = [];
	public availableVopAudioInputChannels: AudioInputChannel[] = [];
	public availableVttAudioInputChannels: AudioInputChannel[] = [];
	public availablePhoneAudioInputChannels: PhoneAudioInputChannel[] = [];
	public availableFlyingAudioInputChannels: AudioInputChannel[] = [];

	public availableVogNetworkInputChannels: NetworkAudioInputChannel[] = [];
	public availableVopNetworkInputChannels: NetworkAudioInputChannel[] = [];
	public availableVttNetworkInputChannels: NetworkAudioInputChannel[] = [];

	public availableVopAudioOutputChannels: AudioOutputChannel[] = [];
	public availableVttAudioOutputChannels: AudioOutputChannel[] = [];

	public availableOutputChannels: AudioOutputChannel[] = [];
	public availableWebsocketOutputChannels: AudioOutputChannel[] = [];

	// phonechannels sind mit id, die anderen mit der channelnummer
	private audioInputChannelDictionary: {[key: string]: AudioInputChannel} = {};
	private phoneAudioInputChannelDictionary: {
		[key: string]: PhoneAudioInputChannel;
	} = {};
	private audioOutputChannelDictionary: {[key: string]: AudioOutputChannel} = {};
	private networkInputChannelDictionary: {
		[key: string]: NetworkAudioInputChannel;
	} = {};

	loadingAudioDeviceChannelsTask: Promise<unknown> = null;

	public filteredVogInputChannels: AudioInputChannel[] = null;
	public filteredVogNetworkInputChannels: NetworkAudioInputChannel[] = null;

	public filteredVopInputChannels: AudioInputChannel[] = null;
	public filteredVopNetworkInputChannels: NetworkAudioInputChannel[] = null;

	public filteredVttInputChannels: AudioInputChannel[] = null;
	public filteredVttNetworkInputChannels: NetworkAudioInputChannel[] = null;

	constructor(
		private _simAudioChannelService: SimAudioChannelService,
		_injector: Injector,
	) {
		super(_injector);
	}

	public ngOnInit() {
		super.ngOnInit();

		this.init();
	}

	protected init() {
		if (this.data.masterOfCeremonyMapping) {
			this.data.masterOfCeremonyMapping.vogInputChannelIds = this.data.masterOfCeremonyMapping.vogInputChannelIds || [];
			this.data.masterOfCeremonyMapping.vopInputChannelIds = this.data.masterOfCeremonyMapping.vopInputChannelIds || [];
			this.data.masterOfCeremonyMapping.vttInputChannelIds = this.data.masterOfCeremonyMapping.vttInputChannelIds || [];
			this.data.masterOfCeremonyMapping.flyingInputchannelIds = this.data.masterOfCeremonyMapping.flyingInputchannelIds || [];

			this.data.masterOfCeremonyMapping.vopOutputChannels = this.data.masterOfCeremonyMapping.vopOutputChannels || [];

			this.data.masterOfCeremonyMapping.phoneInputChannelIds = this.data.masterOfCeremonyMapping.phoneInputChannelIds || [];

			this.directOutputChannelIdFC.setValue(this.data.masterOfCeremonyMapping.audioOutputChannelId);
			this.directOutputChannelIdFC.valueChanges.subscribe((value) => {
				this.data.masterOfCeremonyMapping.audioOutputChannelId = value;
			});

			this.websocketOutputChannelIdFC.setValue(this.data.masterOfCeremonyMapping.websocketAudioOutputChannelId);
			this.websocketOutputChannelIdFC.valueChanges.subscribe((value) => {
				this.data.masterOfCeremonyMapping.websocketAudioOutputChannelId = value;
			});
		}

		this.loadAudioDeviceChannels();
	}

	public saveRecorderMapping() {
		this.close({
			action: "ok",
			updatedMasterOfCeremonyMapping: this.data.masterOfCeremonyMapping,
		});
	}

	public loadAudioDeviceChannels() {
		this.audioInputChannelDictionary = {};
		this.phoneAudioInputChannelDictionary = {};
		this.audioOutputChannelDictionary = {};

		const task1 = this._simAudioChannelService.getAudioInputChannels().then((result) => {
			if (result) {
				if (result?.length > 0) {
					this.availableVogAudioInputChannels = [];
					this.availableVopAudioInputChannels = [];
					this.availableFlyingAudioInputChannels = [];
					this.availableVopAudioOutputChannels = [];
					this.availableVttAudioInputChannels = [];
					this.availableVttAudioOutputChannels = [];

					for (const audioInputChannel of result) {
						// creates a dictionary for all channels
						this.audioInputChannelDictionary[audioInputChannel.id] = audioInputChannel;

						// ------------------------ //
						// Audio channel types:     //
						// ------------------------ //
						// Default              = 0 //
						// VOG                  = 1 //
						// VTP                  = 2 //
						// DefaultOutputChannel = 3 //
						// VogOutputChannel     = 4 //
						// VopOutputChannel     = 5 //
						// VttInputChannel      = 7 //
						// FlyingMic            = 11//
						if (audioInputChannel.type === AudioDeviceInputChannelType.VOG) {
							this.availableVogAudioInputChannels.push(audioInputChannel);
						} else if (audioInputChannel.type === AudioDeviceInputChannelType.VTP) {
							this.availableVopAudioInputChannels.push(audioInputChannel);
						} else if (audioInputChannel.type === AudioDeviceInputChannelType.VttInputChannel) {
							this.availableVttAudioInputChannels.push(audioInputChannel);
						} else if (audioInputChannel.type === AudioDeviceInputChannelType.FlyingMic) {
							this.availableFlyingAudioInputChannels.push(audioInputChannel);
						}
					}
				}
				this.filteredVogInputChannels = null;
				this.filteredVopInputChannels = null;
				this.filteredVttInputChannels = null;
			}
		});

		const task2 = this._simAudioChannelService.getAudioOutputChannels().then((result) => {
			if (result) {
				if (result?.length > 0) {
					this.availableOutputChannels = [];

					for (const audioOutputChannel of result) {
						// creates a dictionary for all channels
						this.audioOutputChannelDictionary[audioOutputChannel.id] = audioOutputChannel;

						// ------------------------ //
						// Audio channel types:     //
						// ------------------------ //
						// Default              = 0 //
						// VOG                  = 1 //
						// VTP                  = 2 //
						// DefaultOutputChannel = 3 //
						// VogOutputChannel     = 4 //
						// VopOutputChannel     = 5 //
						// VttOutputChannel = 8
						if (audioOutputChannel.type === AudioDeviceOutputChannelType.DefaultOutputChannel) {
							this.availableOutputChannels.push(audioOutputChannel);
						} else if (audioOutputChannel.type === AudioDeviceOutputChannelType.VopOutputChannel) {
							this.availableVopAudioOutputChannels.push(audioOutputChannel);
						} else if (audioOutputChannel.type === AudioDeviceOutputChannelType.VttOutputChannel) {
							this.availableVttAudioOutputChannels.push(audioOutputChannel);
						} else if (audioOutputChannel.type == AudioDeviceOutputChannelType.Websocket) {
							this.availableWebsocketOutputChannels.push(audioOutputChannel);
						}
					}
				}
			}
		});

		const task3 = this._simAudioChannelService.getPhoneAudioInputChannels().then((result) => {
			if (result) {
				if (result?.length > 0) {
					this.availablePhoneAudioInputChannels = [];

					for (const phoneAudioInputChannel of result) {
						// creates a dictionary for all channels
						this.phoneAudioInputChannelDictionary[phoneAudioInputChannel.id] = phoneAudioInputChannel;

						// ------------------------ //
						// Audio channel types:     //
						// ------------------------ //
						// PhoneInputChannel     = 9 //
						if (phoneAudioInputChannel.type === AudioDeviceInputChannelType.PhoneInputChannel) {
							this.availablePhoneAudioInputChannels.push(phoneAudioInputChannel);
						}
					}
				}
			}
		});

		const task4 = this._simAudioChannelService.getFilteredNetworkAudioInputChannels().then((result) => {
			if (result) {
				if (result.data?.length > 0) {
					this.availableVogNetworkInputChannels = [];

					for (const networkInputChannel of result.data) {
						// creates a dictionary for all channels
						this.networkInputChannelDictionary[networkInputChannel.id] = networkInputChannel;

						if (networkInputChannel.type === AudioDeviceInputChannelType.VOG) {
							this.availableVogNetworkInputChannels.push(networkInputChannel);
						} else if (networkInputChannel.type === AudioDeviceInputChannelType.VTP) {
							this.availableVopNetworkInputChannels.push(networkInputChannel);
						} else if (networkInputChannel.type === AudioDeviceInputChannelType.VttInputChannel) {
							this.availableVttNetworkInputChannels.push(networkInputChannel);
						}
					}
				}

				this.filteredVogNetworkInputChannels = null;
				this.filteredVopNetworkInputChannels = null;
				this.filteredVttNetworkInputChannels = null;
			}
		});
		this.loadingAudioDeviceChannelsTask = Promise.all([task1, task2, task3, task4]);
	}

	public getInputChannelName(audioInputChannelId: string) {
		if (this.audioInputChannelDictionary[audioInputChannelId]) {
			return this.audioInputChannelDictionary[audioInputChannelId].displayName;
		} else {
			return `Channel not defined: ${audioInputChannelId}`;
		}
	}

	public getNetworkChannelName(channelId: string) {
		if (this.networkInputChannelDictionary[channelId]) {
			return this.networkInputChannelDictionary[channelId].displayName;
		} else {
			return `Channel not defined: ${channelId}`;
		}
	}

	public addChannel(channelId: string, channelIds: string[]) {
		if (channelIds.indexOf(channelId) < 0) {
			channelIds.push(channelId);
		}
		this.resetFilters();
	}

	public removeChannel(channelId: string, channelIds: string[]) {
		channelIds.splice(channelIds.indexOf(channelId), 1);
		this.resetFilters();
	}

	public filterVogInputChannels() {
		this.data.masterOfCeremonyMapping.vogInputChannelIds ??= [];
		if (!!this.availableVogAudioInputChannels) {
			this.filteredVogInputChannels = this.availableVogAudioInputChannels.filter(
				(audioChannel) => !this.data.masterOfCeremonyMapping.vogInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vogInputChannelId = null;

		return this.filteredVogInputChannels;
	}

	public filterVogNetworkInputChannels() {
		this.data.masterOfCeremonyMapping.vogNetworkAudioInputChannelIds ??= [];
		if (!!this.availableVogNetworkInputChannels) {
			this.filteredVogNetworkInputChannels = this.availableVogNetworkInputChannels.filter(
				(audioChannel) => !this.data.masterOfCeremonyMapping.vogNetworkAudioInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vogNetworkInputChannelId = null;

		return this.filteredVogNetworkInputChannels;
	}

	public filterVopInputChannels() {
		this.data.masterOfCeremonyMapping.vopInputChannelIds ??= [];
		if (!!this.availableVopAudioInputChannels) {
			this.filteredVopInputChannels = this.availableVopAudioInputChannels.filter(
				(audioChannel) => !this.data.masterOfCeremonyMapping.vopInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vopInputChannelId = null;

		return this.filteredVopInputChannels;
	}

	public filterVopNetworkInputChannels() {
		this.data.masterOfCeremonyMapping.vopNetworkAudioInputChannelIds ??= [];
		if (!!this.availableVopNetworkInputChannels) {
			this.filteredVopNetworkInputChannels = this.availableVopNetworkInputChannels.filter(
				(audioChannel) => !this.data.masterOfCeremonyMapping.vopNetworkAudioInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vopNetworkInputChannelId = null;

		return this.filteredVopNetworkInputChannels;
	}

	public filterVttInputChannels() {
		this.data.masterOfCeremonyMapping.vttInputChannelIds ??= [];
		if (!!this.availableVttAudioInputChannels) {
			this.filteredVttInputChannels = this.availableVttAudioInputChannels.filter(
				(audioChannel) => !this.data.masterOfCeremonyMapping.vttInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vttInputChannelId = null;

		return this.filteredVttInputChannels;
	}

	public filterVttNetworkInputChannels() {
		this.data.masterOfCeremonyMapping.vttNetworkAudioInputChannelIds ??= [];
		if (!!this.availableVttNetworkInputChannels) {
			this.filteredVttNetworkInputChannels = this.availableVttNetworkInputChannels.filter(
				(audioChannel) => !this.data.masterOfCeremonyMapping.vttNetworkAudioInputChannelIds.includes(audioChannel.id),
			);
		}
		this.vttNetworkInputChannelId = null;

		return this.filteredVttNetworkInputChannels;
	}

	private resetFilters() {
		this.filteredVogInputChannels = null;
		this.filteredVogNetworkInputChannels = null;
	}
}
